/**
 * CRM Tool
 * AI-powered CRM assistant with tabbed reports
 * Tab 1: AI Assistant (chat)
 * Tab 2: 30 Day Close report
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Textarea,
  Spinner,
  Card,
  CardHeader,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Tab,
  TabList,
  TabValue,
  SelectTabEvent,
  SelectTabData,
  Field,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  Checkbox,
} from "@fluentui/react-components";
import {
  Send24Regular,
  Add24Regular,
  Delete24Regular,
  History24Regular,
  Chat24Regular,
  MoreHorizontal24Regular,
  Edit24Regular,
  Dismiss24Regular,
  People24Regular,
  DocumentTable24Regular,
  Target24Regular,
  Phone24Regular,
  PersonAdd24Regular,
  ChevronRight16Regular,
  ChevronDown16Regular,
} from "@fluentui/react-icons";

import useCrmApiClient from "../../hooks/useCrmApiClient";
import useUsageTracking from "../../hooks/useUsageTracking";
import { VoiceTextInputButton } from "../../components/VoiceTextInputButton";
import { getConfig } from "../../config";
import { TackleMyDayPanel } from "./TackleMyDayPanel";
import { TmdLeaderboard } from "./TmdLeaderboard";
import { CallsPanel } from "./CallsPanel";
import { NewLead } from "./NewLead";
import { onBrokerEvent } from "./photonDialBridge";

// ============================================================================
// Styles
// ============================================================================

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  headerIcon: {
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#4CAF50",
    borderRadius: "6px",
    color: tokens.colorNeutralForegroundOnBrand,
  },
  tabList: {
    padding: `0 ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  // Calls tab when an unacknowledged inbound call is ringing/in-progress.
  // Force red on the tab text + icon. Fluent UI Tab paints inner slots with
  // neutral colors via Griffel atomic classes — !important is the simplest
  // reliable way to override without coupling to Fluent's internal slot
  // class names. Cleared when the user opens the Calls tab.
  tabAlert: {
    color: `${tokens.colorPaletteRedForeground1} !important`,
    '& *': {
      color: `${tokens.colorPaletteRedForeground1} !important`,
    },
  },
  content: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
  },
  chatContainer: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
  },
  sidebar: {
    width: "280px",
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    flexDirection: "column",
  },
  sidebarHeader: {
    padding: tokens.spacingVerticalM,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  conversationList: {
    flex: 1,
    overflowY: "auto",
    padding: tokens.spacingVerticalS,
  },
  conversationItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalS,
    borderRadius: "6px",
    cursor: "pointer",
    marginBottom: "4px",
    transition: "background-color 0.1s",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  conversationItemActive: {
    backgroundColor: tokens.colorBrandBackground2,
    ":hover": {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  conversationInfo: {
    flex: 1,
    overflow: "hidden",
  },
  conversationTitle: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "13px",
    fontWeight: 500,
  },
  conversationPreview: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    marginTop: "2px",
  },
  mainChat: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  messagesContainer: {
    flex: 1,
    overflowY: "auto",
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
  },
  message: {
    marginBottom: tokens.spacingVerticalM,
    maxWidth: "800px",
  },
  messageUser: {
    marginLeft: "auto",
  },
  messageBubble: {
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderRadius: "12px",
    fontSize: "14px",
    lineHeight: "1.5",
  },
  messageBubbleUser: {
    backgroundColor: "#4CAF50",
    color: "#fff",
    borderBottomRightRadius: "4px",
    whiteSpace: "pre-wrap",
  },
  messageBubbleAssistant: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderBottomLeftRadius: "4px",
    "& p": {
      margin: "4px 0",
    },
    "& p:last-child": { margin: 0 },
    "& h1, & h2, & h3, & h4": { margin: "12px 0 4px 0", fontWeight: 600 },
    "& ul, & ol": {
      margin: "4px 0",
      paddingLeft: "24px",
    },
    "& li": {
      marginBottom: "2px",
    },
    "& li > p": {
      margin: "0",
    },
    "& code": {
      fontFamily: "monospace",
      backgroundColor: tokens.colorNeutralBackground3,
      padding: "1px 4px",
      borderRadius: "3px",
      fontSize: "12px",
    },
    "& pre": {
      backgroundColor: tokens.colorNeutralBackground3,
      padding: "8px 12px",
      borderRadius: "4px",
      overflowX: "auto",
      fontSize: "12px",
    },
    "& strong": { fontWeight: 600 },
    "& hr": {
      border: "none",
      borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
      margin: "8px 0",
    },
    "& table": {
      borderCollapse: "collapse" as const,
      width: "100%",
      margin: "8px 0 12px 0",
      fontSize: "13px",
    },
    "& th": {
      backgroundColor: tokens.colorNeutralBackground3,
      fontWeight: 600,
      padding: "6px 12px",
      textAlign: "left" as const,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    "& td": {
      padding: "5px 12px",
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      verticalAlign: "top",
    },
    "& tr:nth-child(even) td": {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  messageLabel: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    marginBottom: "4px",
  },
  inputContainer: {
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  inputRow: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "flex-end",
  },
  inputField: {
    flex: 1,
  },
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
  emptyStateIcon: {
    fontSize: "48px",
    marginBottom: tokens.spacingVerticalM,
  },
  notConfigured: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingVerticalXXL,
  },
  toolCallBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "2px 8px",
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: "4px",
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    marginBottom: "4px",
    fontFamily: "monospace",
  },
  // Report styles
  reportContainer: {
    flex: 1,
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    overflowY: "auto",
  },
  reportCard: {
    maxWidth: "800px",
    marginBottom: tokens.spacingVerticalL,
  },
  reportForm: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalL,
    maxWidth: "100%",
  },
  reportFormRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: tokens.spacingHorizontalL,
    flexWrap: "wrap",
  },
  reportTable: {
    maxWidth: "100%",
    overflowX: "auto",
  },
  reportResults: {
    marginTop: tokens.spacingVerticalL,
  },
  reportSummary: {
    marginBottom: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: "6px",
  },
  reportPlaceholder: {
    padding: tokens.spacingVerticalXXL,
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: "8px",
    border: `2px dashed ${tokens.colorNeutralStroke1}`,
  },
  staleBadge: {
    backgroundColor: "#ffcccc",
    padding: "2px 8px",
    borderRadius: "4px",
    display: "inline-block",
    fontSize: "12px",
  },
  warningBadge: {
    backgroundColor: "#fff3cd",
    color: "#856404",
    padding: "1px 6px",
    borderRadius: "4px",
    display: "inline-block",
    fontSize: "11px",
    marginLeft: "6px",
    cursor: "default",
  },
  addNoteToggle: {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: tokens.colorBrandForegroundLink,
    fontSize: "12px",
    fontFamily: "inherit",
  },
  addNoteBlock: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  addNoteActions: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
  },
  addNoteError: {
    fontSize: "12px",
    color: tokens.colorPaletteRedForeground1,
  },
  checkinNoteList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
});

// ============================================================================
// Types
// ============================================================================

interface Message {
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
  toolCalls?: { name: string; args: string }[];
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  message_count: number;
  created_at: string;
  updated_at: string;
}

interface ConversationSummary {
  id: string;
  title: string;
  message_count: number;
  created_at: string;
  updated_at: string;
  preview?: string;
}

interface LertMember {
  id: string;
  name: string;
}

const LERT_MEMBERS: LertMember[] = [
  { id: "rw3eTinAcFoPkPtER17Q", name: "Greg Kogler" },
  { id: "wLvGKPXmNGEgbUth0Tft", name: "Jeff Mason" },
  { id: "pARziPrBuAN3UNGRIoiB", name: "Mark Wilk" },
  { id: "w8pEazGuboTDYsMLF369", name: "Matthew Kerlin" },
  { id: "5q88CzziQ6lapZVnQslk", name: "Michael Natale" },
  { id: "rSLfkyXoHsBd32VAEBjC", name: "Travis Martinez" },
  { id: "hGhX4ULvb6T6kcmu1GZT", name: "William Craig" },
];

const GHL_BASE = "https://crm.coto.services/v2/location/OZ0gNsIxzemC6UfTxrH2";

interface SalesOverviewOpp {
  id: string;
  name: string;
  description: string;
  contact_id: string;
  stage: string;
  monetary_value: number | null;
  days_in_stage: number;
  is_stale: boolean;
  stale_reason?: string;
  contact: string;
  missing_fields: string[];
  expected_close_date: string | null;
  close_date_warning: boolean;
  camera_vendor: string;
  demo_status: string;
  pilot_stage: string;
  has_deal_sheet: boolean;
  notes: Array<{ body: string; date: string; user: string }>;
  source: string;
  urgency: "30" | "60" | "90" | "other";
}

interface CheckinTask {
  id: string;
  title: string;
  agency: string;
  due_date: string;
  contact: string;
  contact_id: string;
  notes?: Array<{ body: string; date: string; user: string }>;
}

interface PilotOpp {
  id: string;
  name: string;
  contact_id: string;
  stage: string;
  monetary_value: number | null;
  days_in_stage: number;
  contact: string;
}

interface OperationOpp {
  id: string;
  name: string;
  contact_id: string;
  stage: string;
  monetary_value: number | null;
  days_in_stage: number;
  contact: string;
}

interface SalesOverviewGroup {
  assigned_to: string;
  opp_count: number;
  stale_count: number;
  opportunities: SalesOverviewOpp[];
  pilots: PilotOpp[];
  pilot_count: number;
  operations: OperationOpp[];
  operation_count: number;
  upcoming_pilots: Array<{ id: string; name: string; contact_id: string; contact: string }>;
  upcoming_pilot_count: number;
  checkin_completed: number;
  checkin_remaining: number;
  next_week_completed: number;
  next_week_remaining: number;
  open_checkins: CheckinTask[];
  completed_checkins: CheckinTask[];
}

interface SalesOverviewReport {
  report_date: string;
  pipeline: string;
  total_count: number;
  stale_count: number;
  groups: SalesOverviewGroup[];
  stale_rules?: Array<{ stage: string; rule: string }>;
}

// ============================================================================
// Chat Panel (extracted from original CRM component)
// ============================================================================

interface ChatPanelProps {
  crmApi: ReturnType<typeof useCrmApiClient>;
  /** One-shot prompt to pre-fill the textarea on mount (e.g. from TMD's "Ask Photon" button). */
  pendingInput?: string | null;
  onConsumePendingInput?: () => void;
  /** One-shot title applied to the first conversation created after pendingInput lands. */
  pendingTitle?: string | null;
  onConsumePendingTitle?: () => void;
  /** When true, the pending input is sent immediately instead of just pre-filling the textarea.
   *  Used by the inbound-call screen-pop flow where the user wants the brief without an extra click. */
  pendingAutoSend?: boolean;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ crmApi, pendingInput, onConsumePendingInput, pendingTitle, onConsumePendingTitle, pendingAutoSend }) => {
  const styles = useStyles();

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [streamingResponse, setStreamingResponse] = useState<string>("");
  const [streamingStatus, setStreamingStatus] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [editTitleValue, setEditTitleValue] = useState("");

  useEffect(() => {
    loadConversations();
  }, []);

  // One-shot: when a pending input is handed in (from TMD's Ask-Photon button),
  // start a fresh conversation and pre-fill the textarea, then clear it in
  // the parent so it doesn't replay on subsequent mounts.
  const consumedPendingRef = useRef(false);
  // Title that should be applied to the conversation created by the next
  // sendMessage — captured off props so it survives even if the parent
  // clears the pending state before the SSE stream starts.
  const pendingTitleRef = useRef<string | null>(null);
  useEffect(() => {
    if (consumedPendingRef.current) return;
    if (pendingInput && pendingInput.trim()) {
      consumedPendingRef.current = true;
      setCurrentConversation(null);
      if (pendingTitle && pendingTitle.trim()) {
        pendingTitleRef.current = pendingTitle.trim().slice(0, 200);
        onConsumePendingTitle?.();
      }
      onConsumePendingInput?.();
      if (pendingAutoSend) {
        // Skip the input pre-fill; just fire it off.
        void sendMessage(pendingInput);
      } else {
        setInputValue(pendingInput);
      }
    }
  }, [pendingInput, pendingTitle, pendingAutoSend, onConsumePendingInput, onConsumePendingTitle]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [currentConversation?.messages, streamingResponse, streamingStatus, pendingMessage]);

  useEffect(() => {
    if (textareaRef.current && !editingTitle) {
      textareaRef.current.focus();
    }
  }, [isSending, currentConversation?.id, editingTitle]);

  const crmFetch = useCallback(async (path: string, options?: RequestInit) => {
    const authHeaders = await crmApi.getAuthHeaders();
    const url = `${crmApi.getBaseUrl()}${path}`;
    let response = await fetch(url, {
      ...options,
      headers: { ...authHeaders, ...options?.headers } as HeadersInit,
    });
    if (response.status === 401) {
      const retryHeaders = await crmApi.getAuthHeaders(true);
      response = await fetch(url, {
        ...options,
        headers: { ...retryHeaders, ...options?.headers } as HeadersInit,
      });
    }
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return response;
  }, [crmApi]);

  const loadConversations = async () => {
    setIsLoading(true);
    try {
      const resp = await crmFetch("/api/conversations");
      const data = await resp.json();
      setConversations(data || []);
    } catch (error) {
      console.error("Failed to load conversations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadConversation = async (id: string) => {
    setIsLoading(true);
    try {
      const resp = await crmFetch(`/api/conversations/${id}`);
      const data = await resp.json();
      setCurrentConversation(data);
    } catch (error) {
      console.error("Failed to load conversation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const createNewConversation = () => {
    setCurrentConversation(null);
    setInputValue("");
  };

  const sendMessage = async (messageOverride?: string) => {
    const messageContent = (messageOverride !== undefined ? messageOverride : inputValue).trim();
    if (!messageContent || isSending) return;
    setInputValue("");
    setIsSending(true);
    setPendingMessage(messageContent);
    setStreamingResponse("");
    setStreamingStatus(null);
    setErrorMessage(null);

    try {
      let authHeaders = await crmApi.getAuthHeaders();
      let response = await fetch(`${crmApi.getBaseUrl()}/api/chat/send/stream`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          message: messageContent,
          conversation_id: currentConversation?.id,
        }),
      });

      if (response.status === 401) {
        authHeaders = await crmApi.getAuthHeaders(true);
        response = await fetch(`${crmApi.getBaseUrl()}/api/chat/send/stream`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({
            message: messageContent,
            conversation_id: currentConversation?.id,
          }),
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let conversationId: string | null = null;
      let fullResponse = "";
      // Buffer incomplete SSE lines — JSON payloads can span TCP chunks; without this,
      // JSON.parse fails and the client never receives `text` / `done`, leaving the UI spinning.
      let sseLineBuffer = "";

      const handleSSEPayload = async (raw: string) => {
        if (!raw) return;
        let data: any;
        try {
          data = JSON.parse(raw);
        } catch {
          console.warn("Failed to parse SSE data:", raw.slice(0, 200));
          return;
        }

        if (data.type === "init") {
          conversationId = data.conversation_id;
          // Apply pending title (from TMD's Ask-Photon handoff) to the new
          // conversation so the left-sidebar entry shows the opp name, not
          // the "ABSOLUTE READ-ONLY..." prefix from the hardened prompt.
          if (conversationId && pendingTitleRef.current) {
            const t = pendingTitleRef.current;
            pendingTitleRef.current = null;
            try {
              await crmApi.patch(`/api/conversations/${conversationId}`, { title: t });
            } catch (err) {
              console.warn("Failed to set conversation title from TMD handoff:", err);
            }
          }
        } else if (data.type === "text") {
          fullResponse += data.content;
          setStreamingResponse(fullResponse);
        } else if (data.type === "status") {
          setStreamingStatus(data.content);
        } else if (data.type === "tool_start") {
          setStreamingStatus(`Calling CRM API: ${data.endpoint || data.query || "..."}...`);
        } else if (data.type === "tool_end") {
          setStreamingStatus(data.rows ? `API returned ${data.rows} result(s)` : null);
        } else if (data.type === "done") {
          setStreamingStatus(null);
          if (conversationId) {
            await loadConversation(conversationId);
          }
          await loadConversations();
        } else if (data.type === "error") {
          throw new Error(data.content || "Server error");
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        sseLineBuffer += decoder.decode(value ?? new Uint8Array(), { stream: !done });

        let newlineIdx: number;
        while ((newlineIdx = sseLineBuffer.indexOf("\n")) >= 0) {
          const line = sseLineBuffer.slice(0, newlineIdx).replace(/\r$/, "");
          sseLineBuffer = sseLineBuffer.slice(newlineIdx + 1);
          if (line.startsWith("data: ")) {
            const payload = line.slice(6).trim();
            await handleSSEPayload(payload);
          }
        }

        if (done) {
          if (sseLineBuffer.trim()) {
            const line = sseLineBuffer.replace(/\r$/, "");
            if (line.startsWith("data: ")) {
              await handleSSEPayload(line.slice(6).trim());
            }
          }
          break;
        }
      }

      if (!fullResponse.trim()) {
        throw new Error("No response received. Your session may have expired — try refreshing the page.");
      }
    } catch (error: any) {
      console.error("Failed to send message:", error);
      const message = error?.message || "Failed to send message. Please try again.";
      setErrorMessage(message);
      setInputValue(messageContent);
      setTimeout(() => setErrorMessage(null), 10000);
    } finally {
      setIsSending(false);
      setPendingMessage(null);
      setStreamingResponse("");
      setStreamingStatus(null);
    }
  };

  const deleteConversation = async (id: string) => {
    try {
      await crmFetch(`/api/conversations/${id}`, { method: "DELETE" });
      if (currentConversation?.id === id) {
        setCurrentConversation(null);
      }
      await loadConversations();
    } catch (error) {
      console.error("Failed to delete conversation:", error);
    }
  };

  const updateTitle = async (id: string, title: string) => {
    try {
      await crmFetch(`/api/conversations/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ title }),
      });
      await loadConversations();
      if (currentConversation?.id === id) {
        setCurrentConversation({ ...currentConversation, title });
      }
    } catch (error) {
      console.error("Failed to update title:", error);
    }
    setEditingTitle(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className={styles.chatContainer}>
      {/* Sidebar */}
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <Text weight="semibold">
            <History24Regular style={{ marginRight: 8, verticalAlign: "middle" }} />
            Conversations
          </Text>
          <Button
            appearance="subtle"
            icon={<Add24Regular />}
            onClick={createNewConversation}
            title="New conversation"
          />
        </div>
        <div className={styles.conversationList}>
          {isLoading && conversations.length === 0 ? (
            <Spinner size="small" label="Loading..." />
          ) : conversations.length === 0 ? (
            <Text size={200} style={{ padding: "16px", color: tokens.colorNeutralForeground3 }}>
              No conversations yet. Start a new one!
            </Text>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.id}
                className={`${styles.conversationItem} ${
                  currentConversation?.id === conv.id ? styles.conversationItemActive : ""
                }`}
                onClick={() => loadConversation(conv.id)}
              >
                <div className={styles.conversationInfo}>
                  {editingTitle === conv.id ? (
                    <Input
                      size="small"
                      value={editTitleValue}
                      onChange={(e, data) => setEditTitleValue(data.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") updateTitle(conv.id, editTitleValue);
                        else if (e.key === "Escape") setEditingTitle(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <>
                      <div className={styles.conversationTitle}>{conv.title}</div>
                      {conv.preview && (
                        <div className={styles.conversationPreview}>{conv.preview}</div>
                      )}
                    </>
                  )}
                </div>
                <Menu>
                  <MenuTrigger>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<MoreHorizontal24Regular />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem
                        icon={<Edit24Regular />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingTitle(conv.id);
                          setEditTitleValue(conv.title);
                        }}
                      >
                        Rename
                      </MenuItem>
                      <MenuItem
                        icon={<Delete24Regular />}
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteConversation(conv.id);
                        }}
                      >
                        Delete
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main chat area */}
      <div className={styles.mainChat}>
        {currentConversation ? (
          <div className={styles.messagesContainer} ref={messagesContainerRef}>
            {currentConversation.messages.map((msg, idx) => (
              <div
                key={idx}
                className={`${styles.message} ${msg.role === "user" ? styles.messageUser : ""}`}
              >
                <div className={styles.messageLabel}>
                  {msg.role === "user" ? "You" : "CRM Assistant"}
                </div>
                {msg.toolCalls?.map((tc, i) => (
                  <div key={i} className={styles.toolCallBadge}>
                    {tc.name}: {tc.args}
                  </div>
                ))}
                <div
                  className={`${styles.messageBubble} ${
                    msg.role === "user" ? styles.messageBubbleUser : styles.messageBubbleAssistant
                  }`}
                >
                  {msg.role === "user" ? msg.content : (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                  )}
                </div>
              </div>
            ))}
            {isSending && pendingMessage && (
              <>
                <div className={`${styles.message} ${styles.messageUser}`}>
                  <div className={styles.messageLabel}>You</div>
                  <div className={`${styles.messageBubble} ${styles.messageBubbleUser}`}>
                    {pendingMessage}
                  </div>
                </div>
                <div className={styles.message}>
                  <div className={styles.messageLabel}>CRM Assistant</div>
                  <div className={`${styles.messageBubble} ${styles.messageBubbleAssistant}`}>
                    {streamingResponse ? (
                      <>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingResponse}</ReactMarkdown>
                        {streamingStatus && (
                          <div style={{
                            marginTop: 8,
                            padding: "4px 8px",
                            backgroundColor: tokens.colorNeutralBackground3,
                            borderRadius: "4px",
                            fontSize: "12px",
                            color: tokens.colorNeutralForeground3,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}>
                            <Spinner size="extra-tiny" />
                            {streamingStatus}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Spinner size="tiny" />
                        <span>{streamingStatus || "Thinking..."}</span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            <div ref={messagesEndRef} />
          </div>
        ) : isSending ? (
          <div className={styles.messagesContainer} ref={messagesContainerRef}>
            {pendingMessage && (
              <div className={`${styles.message} ${styles.messageUser}`}>
                <div className={styles.messageLabel}>You</div>
                <div className={`${styles.messageBubble} ${styles.messageBubbleUser}`}>
                  {pendingMessage}
                </div>
              </div>
            )}
            <div className={styles.message}>
              <div className={styles.messageLabel}>CRM Assistant</div>
              <div className={`${styles.messageBubble} ${styles.messageBubbleAssistant}`}>
                {streamingResponse ? (
                  <>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingResponse}</ReactMarkdown>
                    {streamingStatus && (
                      <div style={{
                        marginTop: 8,
                        padding: "4px 8px",
                        backgroundColor: tokens.colorNeutralBackground3,
                        borderRadius: "4px",
                        fontSize: "12px",
                        color: tokens.colorNeutralForeground3,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}>
                        <Spinner size="extra-tiny" />
                        {streamingStatus}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Spinner size="tiny" />
                    <span>{streamingStatus || "Querying CRM..."}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <People24Regular className={styles.emptyStateIcon} />
            <Text size={500} weight="semibold">CRM Assistant</Text>
            <Text size={300} style={{ marginTop: 8, maxWidth: 400, textAlign: "center" }}>
              Ask questions about contacts, deals, pipelines, appointments, and more.
              The assistant can query and update your CRM data.
            </Text>
          </div>
        )}

        {errorMessage && (
          <div style={{
            padding: "8px 16px",
            backgroundColor: tokens.colorPaletteRedBackground2,
            color: tokens.colorPaletteRedForeground1,
            borderRadius: "4px",
            margin: "0 16px 8px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <Text size={200}>{errorMessage}</Text>
            <Button
              appearance="subtle"
              size="small"
              icon={<Dismiss24Regular />}
              onClick={() => setErrorMessage(null)}
            />
          </div>
        )}

        <div className={styles.inputContainer}>
          <div className={styles.inputRow}>
            <Textarea
              textarea={{ ref: textareaRef }}
              className={styles.inputField}
              placeholder="Ask about contacts, deals, pipelines..."
              value={inputValue}
              onChange={(e, data) => setInputValue(data.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              resize="vertical"
            />
            <VoiceTextInputButton
              value={inputValue}
              onChange={setInputValue}
              onDictationComplete={(text) => void sendMessage(text)}
              disabled={isSending}
            />
            <Button
              appearance="primary"
              icon={<Send24Regular />}
              onClick={() => void sendMessage()}
              disabled={!inputValue.trim() || isSending}
              style={{ backgroundColor: "#4CAF50" }}
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};



// ============================================================================
// Sales Overview Panel
// ============================================================================

interface SalesOverviewPanelProps {
  crmApi: ReturnType<typeof useCrmApiClient>;
}

const SalesOverviewPanel: React.FC<SalesOverviewPanelProps> = ({ crmApi }) => {
  const styles = useStyles();

  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState<SalesOverviewReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedLert, setSelectedLert] = useState<string>("All");
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());

  type NoteEditor = { open: boolean; text: string; busy: boolean; error?: string; completeOnSave?: boolean };
  const [noteState, setNoteState] = useState<Record<string, NoteEditor>>({});

  type CompleteEditor = { busy: boolean; error?: string };
  const [completeState, setCompleteState] = useState<Record<string, CompleteEditor>>({});

  const setNoteField = (rowKey: string, patch: Partial<NoteEditor>) => {
    setNoteState(prev => {
      const cur = prev[rowKey] || { open: false, text: "", busy: false };
      return { ...prev, [rowKey]: { ...cur, ...patch } };
    });
  };

  const setCompleteField = (taskId: string, patch: Partial<CompleteEditor>) => {
    setCompleteState(prev => {
      const cur = prev[taskId] || { busy: false };
      return { ...prev, [taskId]: { ...cur, ...patch } };
    });
  };

  // Monday-of-this-week (UTC), matching the backend's _get_week_bounds.
  const mondayOfThisWeek = (): Date => {
    const now = new Date();
    const utcMid = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const dow = utcMid.getUTCDay(); // 0=Sun, 1=Mon, ...
    const offset = dow === 0 ? 6 : dow - 1;
    utcMid.setUTCDate(utcMid.getUTCDate() - offset);
    return utcMid;
  };

  const checkinWeekKind = (dueDate: string): "this" | "next" | "other" => {
    if (!dueDate) return "other";
    const due = new Date(`${dueDate}T00:00:00Z`);
    const thisStart = mondayOfThisWeek();
    const nextStart = new Date(thisStart);
    nextStart.setUTCDate(nextStart.getUTCDate() + 7);
    const afterNext = new Date(thisStart);
    afterNext.setUTCDate(afterNext.getUTCDate() + 14);
    if (due >= thisStart && due < nextStart) return "this";
    if (due >= nextStart && due < afterNext) return "next";
    return "other";
  };

  const moveCheckinToCompleted = (taskId: string) => {
    setReportData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        groups: prev.groups.map(g => {
          const idx = g.open_checkins.findIndex(t => t.id === taskId);
          if (idx < 0) return g;
          const task = g.open_checkins[idx];
          const remainingOpen = g.open_checkins.filter(t => t.id !== taskId);
          const week = checkinWeekKind(task.due_date);
          let { checkin_completed, checkin_remaining, next_week_completed, next_week_remaining } = g;
          if (week === "this") {
            checkin_completed += 1;
            checkin_remaining = Math.max(0, checkin_remaining - 1);
          } else if (week === "next") {
            next_week_completed += 1;
            next_week_remaining = Math.max(0, next_week_remaining - 1);
          }
          return {
            ...g,
            open_checkins: remainingOpen,
            completed_checkins: [task, ...g.completed_checkins],
            checkin_completed,
            checkin_remaining,
            next_week_completed,
            next_week_remaining,
          };
        }),
      };
    });
  };

  const markCheckinComplete = async (task: CheckinTask): Promise<boolean> => {
    // Location-scoped completion works even for check-in tasks with no contact,
    // so only the task id is required.
    if (!task.id) return false;
    setCompleteField(task.id, { busy: true, error: undefined });
    try {
      await crmApi.post("/api/sales-overview/complete-checkin", {
        task_id: task.id,
      });
      moveCheckinToCompleted(task.id);
      setCompleteField(task.id, { busy: false });
      return true;
    } catch (e) {
      setCompleteField(task.id, {
        busy: false,
        error: e instanceof Error ? e.message : String(e),
      });
      return false;
    }
  };

  const prependNoteToRow = (
    kind: "opp" | "checkin",
    sourceId: string,
    note: { body: string; date: string; user: string },
  ) => {
    setReportData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        groups: prev.groups.map(g => {
          if (kind === "opp") {
            return {
              ...g,
              opportunities: g.opportunities.map(o =>
                o.id === sourceId ? { ...o, notes: [note, ...o.notes] } : o,
              ),
            };
          }
          return {
            ...g,
            open_checkins: g.open_checkins.map(t =>
              t.id === sourceId ? { ...t, notes: [note, ...(t.notes || [])] } : t,
            ),
          };
        }),
      };
    });
  };

  const saveNote = async (
    rowKey: string,
    contactId: string,
    kind: "opp" | "checkin",
    sourceId: string,
    checkinTask?: CheckinTask,
  ) => {
    const cur = noteState[rowKey];
    const text = (cur?.text || "").trim();
    if (!text) return;
    if (!contactId) {
      setNoteField(rowKey, { error: "No contact on this row to attach a note to." });
      return;
    }
    setNoteField(rowKey, { busy: true, error: undefined });
    try {
      const resp = await crmApi.post<{
        success: boolean;
        note: { body: string; date: string; user: string };
      }>("/api/sales-overview/contact-note", { contact_id: contactId, body: text });
      prependNoteToRow(kind, sourceId, resp.note);
      // Sequenced: if the user also asked to mark the check-in complete,
      // do it after the note lands so attribution is preserved on the note
      // even if the complete call fails (the task stays open with the note
      // already saved).
      if (kind === "checkin" && cur?.completeOnSave && checkinTask) {
        const ok = await markCheckinComplete(checkinTask);
        if (!ok) {
          setNoteField(rowKey, { busy: false });
          return;
        }
      }
      setNoteField(rowKey, { open: false, text: "", busy: false, error: undefined, completeOnSave: false });
    } catch (e) {
      setNoteField(rowKey, {
        busy: false,
        error: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const toggleNotes = (oppId: string) => {
    setExpandedNotes(prev => {
      const next = new Set(prev);
      if (next.has(oppId)) next.delete(oppId);
      else next.add(oppId);
      return next;
    });
  };

  const renderAddNoteEditor = (
    rowKey: string,
    contactId: string,
    kind: "opp" | "checkin",
    sourceId: string,
    checkinTask?: CheckinTask,
  ) => {
    const ns = noteState[rowKey] || { open: false, text: "", busy: false };
    return (
      <div className={styles.addNoteBlock}>
        <Textarea
          value={ns.text}
          onChange={(_, data) => setNoteField(rowKey, { text: data.value })}
          placeholder="Write a note on this contact..."
          resize="vertical"
          rows={3}
          disabled={ns.busy}
        />
        {kind === "checkin" && checkinTask && (
          <Checkbox
            label="Also mark check-in complete"
            checked={Boolean(ns.completeOnSave)}
            onChange={(_, data) => setNoteField(rowKey, { completeOnSave: Boolean(data.checked) })}
            disabled={ns.busy}
          />
        )}
        <div className={styles.addNoteActions}>
          <Button
            appearance="primary"
            size="small"
            disabled={ns.busy || !ns.text.trim()}
            onClick={() => saveNote(rowKey, contactId, kind, sourceId, checkinTask)}
          >
            {ns.busy ? <Spinner size="tiny" /> : (ns.completeOnSave && kind === "checkin" ? "Save & complete" : "Save note")}
          </Button>
          <Button
            appearance="secondary"
            size="small"
            disabled={ns.busy}
            onClick={() => setNoteField(rowKey, { open: false, text: "", error: undefined, completeOnSave: false })}
          >
            Cancel
          </Button>
          {ns.error && <span className={styles.addNoteError}>{ns.error}</span>}
        </div>
      </div>
    );
  };

  const renderOppNotesCell = (opp: SalesOverviewOpp) => {
    const rowKey = `opp:${opp.id}`;
    const ns = noteState[rowKey] || { open: false, text: "", busy: false };
    return (
      <div>
        {renderNotesCell(opp.id, opp.notes)}
        <div style={{ marginTop: 6 }}>
          {!ns.open ? (
            <button
              type="button"
              className={styles.addNoteToggle}
              onClick={() => setNoteField(rowKey, { open: true })}
              disabled={!opp.contact_id}
              title={opp.contact_id ? "Add a note on this contact" : "No contact to attach a note to"}
            >
              + Add note
            </button>
          ) : (
            renderAddNoteEditor(rowKey, opp.contact_id, "opp", opp.id)
          )}
        </div>
      </div>
    );
  };

  const renderCheckinNotesCell = (task: CheckinTask) => {
    const rowKey = `chk:${task.id}`;
    const ns = noteState[rowKey] || { open: false, text: "", busy: false };
    const cs = completeState[task.id] || { busy: false };
    if (!ns.open) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              className={styles.addNoteToggle}
              onClick={() => setNoteField(rowKey, { open: true })}
              disabled={!task.contact_id || cs.busy}
              title={task.contact_id ? "See recent notes and add a new one" : "No contact to attach a note to"}
            >
              + Add note
            </button>
            <button
              type="button"
              className={styles.addNoteToggle}
              onClick={() => markCheckinComplete(task)}
              disabled={cs.busy}
              title="Mark this check-in call complete in GHL"
            >
              {cs.busy ? "Marking…" : "✓ Mark complete"}
            </button>
          </div>
          {cs.error && <span className={styles.addNoteError}>{cs.error}</span>}
        </div>
      );
    }
    const recent = task.notes || [];
    return (
      <div>
        {recent.length > 0 && (
          <div className={styles.checkinNoteList}>
            {recent.map((n, idx) => (
              <div key={idx}>
                <div style={{ fontSize: "11px", fontWeight: 600, color: tokens.colorNeutralForeground2, marginBottom: 2 }}>
                  {n.date && new Date(n.date).toLocaleDateString()}
                  {n.date && n.user && " · "}
                  {n.user}
                </div>
                <div style={{ fontSize: "12px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{n.body}</div>
              </div>
            ))}
          </div>
        )}
        {renderAddNoteEditor(rowKey, task.contact_id, "checkin", task.id, task)}
      </div>
    );
  };

  const renderNotesCell = (rowId: string, notes: Array<{ body: string; date: string; user: string }>) => {
    if (!notes || notes.length === 0) return "-";
    const latest = notes[0];
    const isExpanded = expandedNotes.has(rowId);
    const hasMore = notes.length > 1;
    return (
      <div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 4 }}>
          {hasMore && (
            <button
              onClick={() => toggleNotes(rowId)}
              title={isExpanded ? "Collapse notes" : "Expand notes"}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: tokens.colorNeutralForeground2, display: "flex", alignItems: "center", marginTop: 1 }}
            >
              {isExpanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
            </button>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={isExpanded ? { whiteSpace: "pre-wrap", wordBreak: "break-word" } : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              title={!isExpanded ? latest.body : undefined}
            >
              {latest.body}
            </div>
            {(latest.date || latest.user || hasMore) && (
              <div style={{ fontSize: "11px", color: tokens.colorNeutralForeground3, marginTop: 2 }}>
                {latest.date && new Date(latest.date).toLocaleDateString()}
                {latest.date && latest.user && " · "}
                {latest.user}
                {hasMore && (
                  <>
                    {(latest.date || latest.user) && " · "}
                    <span style={{ fontWeight: 600 }}>{notes.length} notes</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        {isExpanded && hasMore && (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
            {notes.slice(1).map((n, idx) => (
              <div key={idx} style={{ paddingTop: 8, borderTop: `2px solid ${tokens.colorBrandStroke2}` }}>
                <div style={{ fontSize: "12px", fontWeight: 600, color: tokens.colorNeutralForeground2, marginBottom: 4 }}>
                  {n.date && new Date(n.date).toLocaleDateString()}
                  {n.date && n.user && " · "}
                  {n.user}
                </div>
                <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{n.body}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const runReport = async () => {
    setIsLoading(true);
    setError(null);
    setReportData(null);

    try {
      const params = selectedLert && selectedLert !== "All"
        ? `?assigned_to=${encodeURIComponent(selectedLert)}`
        : "";
      const data = await crmApi.get<SalesOverviewReport>(`/api/reports/sales-overview${params}`);
      setReportData(data);
    } catch (err) {
      console.error("Failed to generate report:", err);
      setError(err instanceof Error ? err.message : "Failed to generate report");
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (val: number | null): string => {
    if (val === null || val === undefined) return "-";
    return `$${val.toLocaleString()}`;
  };

  return (
    <div className={styles.reportContainer}>
      <Card className={styles.reportCard}>
        <CardHeader
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <DocumentTable24Regular />
              <Text weight="semibold">Sales Overview</Text>
            </div>
          }
          description="All open opportunities in the Main Pipeline, grouped by assigned LERT Team Member"
        />
      </Card>

      <div className={styles.reportForm}>
        <Field label="LERT Team Member" style={{ maxWidth: "250px" }}>
          <select
            value={selectedLert}
            onChange={(e) => setSelectedLert(e.target.value)}
            style={{
              padding: "6px 12px",
              borderRadius: "4px",
              border: `1px solid ${tokens.colorNeutralStroke1}`,
              backgroundColor: tokens.colorNeutralBackground1,
              fontSize: "14px",
              width: "250px",
            }}
          >
            <option value="All">All</option>
            {LERT_MEMBERS.map((m) => (
              <option key={m.id} value={m.name}>{m.name}</option>
            ))}
          </select>
        </Field>

        <div>
          <Button
            appearance="primary"
            onClick={runReport}
            disabled={isLoading}
            style={{ backgroundColor: "#4CAF50" }}
          >
            {isLoading ? <Spinner size="tiny" /> : "Run Report"}
          </Button>
        </div>
      </div>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalM }}>
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}

      {reportData && (
        <div className={styles.reportResults}>
          <div className={styles.reportSummary}>
            <Text weight="semibold" block>
              Report Generated: {new Date(reportData.report_date).toLocaleString()}
            </Text>
            <Text size={200}>
              Pipeline: {reportData.pipeline} |{" "}
              Total: {reportData.total_count} |{" "}
              Stale: {reportData.stale_count}
            </Text>
          </div>

          {reportData.groups.length === 0 ? (
            <div className={styles.reportPlaceholder}>
              <Text size={400} weight="semibold" block style={{ marginBottom: 8 }}>
                No Results
              </Text>
              <Text size={300}>No open opportunities found in the Main Pipeline.</Text>
            </div>
          ) : (
            reportData.groups.map((group) => (
              <div key={group.assigned_to} style={{ marginBottom: tokens.spacingVerticalL }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: `${tokens.spacingVerticalS} 0`,
                  borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
                  marginBottom: tokens.spacingVerticalS,
                }}>
                  <Text weight="semibold" size={400}>{group.assigned_to}</Text>
                  <Badge appearance="filled" color="informative">{group.opp_count} opps</Badge>
                  {group.stale_count > 0 && (
                    <span className={styles.staleBadge}>{group.stale_count} stale</span>
                  )}
                </div>

                {/* Opportunities Section */}
                <Text weight="semibold" size={300} style={{ display: "block", marginTop: tokens.spacingVerticalM, marginBottom: tokens.spacingVerticalXS, textTransform: "uppercase", letterSpacing: "0.5px", color: tokens.colorNeutralForegroundOnBrand, backgroundColor: tokens.colorBrandBackground, padding: "6px 12px", borderRadius: "4px" }}>
                  Opportunities ({group.opp_count})
                </Text>
                <div>
                  {[
                    { key: "30", label: "Closing within 30 days", color: "#2e7d32", bg: "#f0faf0" },
                    { key: "60", label: "Closing within 60 days", color: "#e67e22", bg: "#fff8f0" },
                    { key: "90", label: "Closing within 90 days", color: "#f1c40f", bg: "#fffdf0" },
                    { key: "other", label: "Other", color: "#dc3545", bg: "#fff5f5" },
                  ].map((bucket) => {
                    const bucketOpps = group.opportunities.filter((o) => o.urgency === bucket.key);
                    if (bucketOpps.length === 0) return null;
                    return (
                      <div key={bucket.key} style={{ marginBottom: tokens.spacingVerticalM }}>
                        <div style={{
                          padding: "4px 10px",
                          backgroundColor: bucket.bg,
                          borderLeft: `4px solid ${bucket.color}`,
                          marginBottom: 4,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}>
                          <Text weight="semibold" size={200} style={{ color: bucket.color }}>{bucket.label}</Text>
                          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>({bucketOpps.length})</Text>
                        </div>
                        <div style={{ overflowX: "auto", maxWidth: "100%" }}>
                  <table style={{ width: "1700px", borderCollapse: "collapse", fontSize: "13px", tableLayout: "fixed" }}>
                    <colgroup>
                      <col style={{ width: "200px" }} />
                      <col style={{ width: "200px" }} />
                      <col style={{ width: "100px" }} />
                      <col style={{ width: "160px" }} />
                      <col style={{ width: "100px" }} />
                      <col style={{ width: "160px" }} />
                      <col style={{ width: "160px" }} />
                      <col style={{ width: "500px" }} />
                    </colgroup>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${tokens.colorNeutralStroke1}`, verticalAlign: "top" }}>
                        <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap", position: "sticky", left: 0, backgroundColor: tokens.colorNeutralBackground1, zIndex: 2, boxShadow: `1px 0 0 ${tokens.colorNeutralStroke1}` }}>Name</th>
                        <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Stage</th>
                        <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Value</th>
                        <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Expected Close</th>
                        <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>
                          Days
                          {reportData.stale_rules && reportData.stale_rules.length > 0 && (
                            <span
                              style={{ marginLeft: 4, cursor: "help", color: tokens.colorNeutralForeground3, fontWeight: "normal" }}
                              title={"Stale rules by stage:\n\n" + reportData.stale_rules.map(r => `• ${r.stage}: ${r.rule}`).join("\n")}
                            >ⓘ</span>
                          )}
                        </th>
                        <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Contact</th>
                        <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Source</th>
                        <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bucketOpps.map((opp) => (
                        <tr key={opp.id} style={{ borderBottom: `1px solid ${tokens.colorNeutralStroke1}`, verticalAlign: "top" }}>
                          <td style={{ padding: "5px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", position: "sticky", left: 0, backgroundColor: tokens.colorNeutralBackground1, zIndex: 1, boxShadow: `1px 0 0 ${tokens.colorNeutralStroke1}` }}>
                            <a href={`${GHL_BASE}/opportunities/${opp.id}?tab=Opportunity+Details`} target="_blank" rel="noopener noreferrer" title={opp.description || opp.name} style={{ color: tokens.colorBrandForeground1, textDecoration: "none" }}>{opp.name}</a>
                          </td>
                          <td style={{ padding: "5px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {opp.stage}
                            {opp.missing_fields && opp.missing_fields.length > 0 && (
                              <span className={styles.warningBadge} title={`Missing: ${opp.missing_fields.join(", ")}`}>
                                ⚠ {opp.missing_fields.length}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: "5px 8px", textAlign: "left", whiteSpace: "nowrap" }}>
                            {formatCurrency(opp.monetary_value)}
                            {(!opp.monetary_value || opp.monetary_value === 0) && (
                              <span className={styles.warningBadge} title="No value set">⚠</span>
                            )}
                          </td>
                          <td style={{ padding: "5px 8px", textAlign: "left", whiteSpace: "nowrap" }}>
                            {opp.expected_close_date || "-"}
                            {opp.close_date_warning && (
                              <span className={styles.warningBadge} title={opp.expected_close_date ? "Close date has passed" : "No close date set"}>⚠</span>
                            )}
                          </td>
                          <td style={{ padding: "5px 8px", textAlign: "left", whiteSpace: "nowrap" }}>
                            {opp.days_in_stage}
                            {opp.is_stale && (
                              <span className={styles.staleBadge} style={{ marginLeft: 6, fontSize: "11px" }} title={opp.stale_reason || "Stale"}>Stale</span>
                            )}
                          </td>
                          <td style={{ padding: "5px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {opp.contact_id ? (
                              <a href={`${GHL_BASE}/contacts/detail/${opp.contact_id}`} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1, textDecoration: "none" }}>{opp.contact}</a>
                            ) : opp.contact}
                          </td>
                          <td style={{ padding: "5px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{opp.source || "-"}</td>
                          <td style={{ padding: "5px 8px", verticalAlign: "top" }}>
                            {renderOppNotesCell(opp)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: `2px solid ${tokens.colorNeutralStroke1}`, backgroundColor: tokens.colorNeutralBackground3, fontWeight: 600 }}>
                        <td style={{ padding: "6px 8px", position: "sticky", left: 0, backgroundColor: tokens.colorNeutralBackground3, zIndex: 1, boxShadow: `1px 0 0 ${tokens.colorNeutralStroke1}` }}>{bucketOpps.length} {bucketOpps.length === 1 ? "opportunity" : "opportunities"}</td>
                        <td></td>
                        <td style={{ padding: "6px 8px", textAlign: "left" }}>{formatCurrency(bucketOpps.reduce((sum, o) => sum + (o.monetary_value || 0), 0))}</td>
                        <td colSpan={5}></td>
                      </tr>
                    </tfoot>
                  </table>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Upcoming Pilots Section */}
                <Text weight="semibold" size={300} style={{ display: "block", marginTop: tokens.spacingVerticalL, marginBottom: tokens.spacingVerticalXS, textTransform: "uppercase", letterSpacing: "0.5px", color: tokens.colorNeutralForegroundOnBrand, backgroundColor: tokens.colorBrandBackground, padding: "6px 12px", borderRadius: "4px" }}>
                  Upcoming Pilots ({group.upcoming_pilot_count || 0})
                </Text>
                {group.upcoming_pilots && group.upcoming_pilots.length > 0 ? (
                  <div className={styles.reportTable}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${tokens.colorNeutralStroke1}`, verticalAlign: "top" }}>
                          <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap", width: "50%" }}>Opportunity</th>
                          <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap", width: "50%" }}>Contact</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.upcoming_pilots.map((up) => (
                          <tr key={up.id} style={{ borderBottom: `1px solid ${tokens.colorNeutralStroke1}`, verticalAlign: "top" }}>
                            <td style={{ padding: "5px 8px" }}>
                              <a href={`${GHL_BASE}/opportunities/${up.id}?tab=Opportunity+Details`} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1, textDecoration: "none" }}>{up.name}</a>
                            </td>
                            <td style={{ padding: "5px 8px" }}>
                              {up.contact_id ? (
                                <a href={`${GHL_BASE}/contacts/detail/${up.contact_id}`} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1, textDecoration: "none" }}>{up.contact}</a>
                              ) : up.contact}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3, fontStyle: "italic" }}>None</Text>
                )}

                {/* Pilots Section */}
                <Text weight="semibold" size={300} style={{ display: "block", marginTop: tokens.spacingVerticalL, marginBottom: tokens.spacingVerticalXS, textTransform: "uppercase", letterSpacing: "0.5px", color: tokens.colorNeutralForegroundOnBrand, backgroundColor: tokens.colorBrandBackground, padding: "6px 12px", borderRadius: "4px" }}>
                  Pilots ({group.pilot_count || 0})
                </Text>
                {group.pilots && group.pilots.length > 0 ? (
                  <div className={styles.reportTable}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${tokens.colorNeutralStroke1}` }}>
                          <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Name</th>
                          <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Stage</th>
                          <th style={{ textAlign: "right", padding: "6px 8px", whiteSpace: "nowrap" }}>Value</th>
                          <th style={{ textAlign: "center", padding: "6px 8px", whiteSpace: "nowrap" }}>Days</th>
                          <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Contact</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.pilots.map((pilot) => (
                          <tr key={pilot.id} style={{ borderBottom: `1px solid ${tokens.colorNeutralStroke1}` }}>
                            <td style={{ padding: "5px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              <a href={`${GHL_BASE}/opportunities/${pilot.id}?tab=Opportunity+Details`} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1, textDecoration: "none" }}>{pilot.name}</a>
                            </td>
                            <td style={{ padding: "5px 8px", whiteSpace: "nowrap" }}>{pilot.stage}</td>
                            <td style={{ padding: "5px 8px", textAlign: "right", whiteSpace: "nowrap" }}>{formatCurrency(pilot.monetary_value)}</td>
                            <td style={{ padding: "5px 8px", textAlign: "center", whiteSpace: "nowrap" }}>{pilot.days_in_stage}</td>
                            <td style={{ padding: "5px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {pilot.contact_id ? (
                                <a href={`${GHL_BASE}/contacts/detail/${pilot.contact_id}`} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1, textDecoration: "none" }}>{pilot.contact}</a>
                              ) : pilot.contact}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3, fontStyle: "italic" }}>None</Text>
                )}

                {/* Operations Section */}
                <Text weight="semibold" size={300} style={{ display: "block", marginTop: tokens.spacingVerticalL, marginBottom: tokens.spacingVerticalXS, textTransform: "uppercase", letterSpacing: "0.5px", color: tokens.colorNeutralForegroundOnBrand, backgroundColor: tokens.colorBrandBackground, padding: "6px 12px", borderRadius: "4px" }}>
                  Operations ({group.operation_count || 0})
                </Text>
                {group.operations && group.operations.length > 0 ? (
                  <div className={styles.reportTable}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${tokens.colorNeutralStroke1}` }}>
                          <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Name</th>
                          <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Stage</th>
                          <th style={{ textAlign: "right", padding: "6px 8px", whiteSpace: "nowrap" }}>Value</th>
                          <th style={{ textAlign: "center", padding: "6px 8px", whiteSpace: "nowrap" }}>Days</th>
                          <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Contact</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.operations.map((op) => (
                          <tr key={op.id} style={{ borderBottom: `1px solid ${tokens.colorNeutralStroke1}` }}>
                            <td style={{ padding: "5px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              <a href={`${GHL_BASE}/opportunities/${op.id}?tab=Opportunity+Details`} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1, textDecoration: "none" }}>{op.name}</a>
                            </td>
                            <td style={{ padding: "5px 8px", whiteSpace: "nowrap" }}>{op.stage}</td>
                            <td style={{ padding: "5px 8px", textAlign: "right", whiteSpace: "nowrap" }}>{formatCurrency(op.monetary_value)}</td>
                            <td style={{ padding: "5px 8px", textAlign: "center", whiteSpace: "nowrap" }}>{op.days_in_stage}</td>
                            <td style={{ padding: "5px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {op.contact_id ? (
                                <a href={`${GHL_BASE}/contacts/detail/${op.contact_id}`} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1, textDecoration: "none" }}>{op.contact}</a>
                              ) : op.contact}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3, fontStyle: "italic" }}>None</Text>
                )}

                {/* Check-Ins Section */}
                <Text weight="semibold" size={300} style={{ display: "block", marginTop: tokens.spacingVerticalL, marginBottom: tokens.spacingVerticalXS, textTransform: "uppercase", letterSpacing: "0.5px", color: tokens.colorNeutralForegroundOnBrand, backgroundColor: tokens.colorBrandBackground, padding: "6px 12px", borderRadius: "4px" }}>
                  Check-Ins
                </Text>
                {group.assigned_to !== "Unassigned" ? (
                  <>
                    <div style={{
                      padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
                      backgroundColor: tokens.colorNeutralBackground3,
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                    }}>
                      <Text weight="semibold" size={300}>This Week:</Text>
                      <Badge appearance="filled" color="success">{group.checkin_completed} completed</Badge>
                      {group.checkin_remaining > 0 && (
                        <Badge appearance="filled" color="warning">{group.checkin_remaining} remaining</Badge>
                      )}
                      {group.checkin_remaining === 0 && group.checkin_completed > 0 && (
                        <Badge appearance="filled" color="success">All done!</Badge>
                      )}
                      {group.checkin_completed === 0 && group.checkin_remaining === 0 && (
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3, fontStyle: "italic" }}>None</Text>
                      )}
                    </div>

                    <div style={{
                      marginTop: tokens.spacingVerticalXS,
                      padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
                      backgroundColor: tokens.colorNeutralBackground3,
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                    }}>
                      <Text weight="semibold" size={300}>Next Week:</Text>
                      <Badge appearance="filled" color="success">{group.next_week_completed} completed</Badge>
                      {group.next_week_remaining > 0 && (
                        <Badge appearance="filled" color="warning">{group.next_week_remaining} remaining</Badge>
                      )}
                      {group.next_week_completed === 0 && group.next_week_remaining === 0 && (
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3, fontStyle: "italic" }}>None</Text>
                      )}
                    </div>

                    {/* Open Check-In Tasks (this week + next week) */}
                    <div style={{ marginTop: tokens.spacingVerticalM }}>
                      <Text weight="semibold" size={300} style={{ marginBottom: tokens.spacingVerticalXS, display: "block" }}>
                        Open Check-Ins ({group.open_checkins.length})
                      </Text>
                      {group.open_checkins.length > 0 ? (
                        <div className={styles.reportTable}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", tableLayout: "fixed" }}>
                            <colgroup>
                              <col style={{ width: "30%" }} />
                              <col style={{ width: "25%" }} />
                              <col style={{ width: "110px" }} />
                              <col />
                            </colgroup>
                            <thead>
                              <tr style={{ borderBottom: `2px solid ${tokens.colorNeutralStroke1}`, verticalAlign: "top" }}>
                                <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Task</th>
                                <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Contact</th>
                                <th style={{ textAlign: "center", padding: "6px 8px", whiteSpace: "nowrap" }}>Due Date</th>
                                <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.open_checkins.map((task) => (
                                <tr key={task.id} style={{ borderBottom: `1px solid ${tokens.colorNeutralStroke1}`, verticalAlign: "top" }}>
                                  <td style={{ padding: "5px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {/* No link on the task name: GHL can't cold-open a task from a URL,
                                        and the Contact column already links to the contact. */}
                                    <span title={task.title}>{task.title}</span>
                                  </td>
                                  <td style={{ padding: "5px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {task.contact_id ? (
                                      <a href={`${GHL_BASE}/contacts/detail/${task.contact_id}`} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1, textDecoration: "none" }}>{task.contact || "-"}</a>
                                    ) : (task.contact || "-")}
                                  </td>
                                  <td style={{ padding: "5px 8px", textAlign: "center", whiteSpace: "nowrap" }}>{task.due_date}</td>
                                  <td style={{ padding: "5px 8px" }}>
                                    {renderCheckinNotesCell(task)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3, fontStyle: "italic" }}>None</Text>
                      )}
                    </div>

                    {/* Completed Check-In Tasks (this week + next week) */}
                    <div style={{ marginTop: tokens.spacingVerticalM }}>
                      <Text weight="semibold" size={300} style={{ marginBottom: tokens.spacingVerticalXS, display: "block" }}>
                        Completed Check-Ins ({group.completed_checkins.length})
                      </Text>
                      {group.completed_checkins.length > 0 ? (
                        <div className={styles.reportTable}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                            <thead>
                              <tr style={{ borderBottom: `2px solid ${tokens.colorNeutralStroke1}` }}>
                                <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Task</th>
                                <th style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>Contact</th>
                                <th style={{ textAlign: "center", padding: "6px 8px", whiteSpace: "nowrap" }}>Due Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.completed_checkins.map((task) => (
                                <tr key={task.id} style={{ borderBottom: `1px solid ${tokens.colorNeutralStroke1}` }}>
                                  <td style={{ padding: "5px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {/* No link on the task name: GHL can't cold-open a task from a URL,
                                        and the Contact column already links to the contact. */}
                                    <span title={task.title}>{task.title}</span>
                                  </td>
                                  <td style={{ padding: "5px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {task.contact_id ? (
                                      <a href={`${GHL_BASE}/contacts/detail/${task.contact_id}`} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1, textDecoration: "none" }}>{task.contact || "-"}</a>
                                    ) : (task.contact || "-")}
                                  </td>
                                  <td style={{ padding: "5px 8px", textAlign: "center", whiteSpace: "nowrap" }}>{task.due_date}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3, fontStyle: "italic" }}>None</Text>
                      )}
                    </div>
                  </>
                ) : (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3, fontStyle: "italic" }}>None</Text>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};


// ============================================================================
// Main Component
// ============================================================================

// Builds the inbound-call brief prompt sent to the AI Assistant when a phone
// call comes in via the Photon Dial extension. The deep-link carries
// caller phone + contactId/name/company; the prompt asks for a focused
// pre-call brief grounded in the contact record (recent activity, open opps,
// risks, talking points) with a hard "report only — no write actions" rail.
function buildInboundCallPrompt(args: {
  from: string;
  contactId: string | null;
  name: string | null;
  company: string | null;
}): string {
  const { from, contactId, name, company } = args;
  const identityLine = name
    ? `${name}${company ? ` at ${company}` : ""}${contactId ? ` (contactId: ${contactId})` : ""}`
    : "an unknown caller (no GHL contact match)";
  const parts: string[] = [];
  parts.push(`I'm about to take an inbound call from ${from} — ${identityLine}.`);
  if (contactId) {
    parts.push(`Look up this contact in GHL and brief me before I answer.`);
  }
  parts.push(
    "Cover (1) who this contact is and their role, (2) recent activity in the last ~30 days (notes, messages, opps), (3) any open opportunities with stage and value, (4) anything sensitive — DND status, escalations, pending issues — and (5) one or two suggested opening points or questions.",
  );
  parts.push(
    "Use the available read tools to fetch notes, conversations, and opportunities directly — do NOT respond with 'I can retrieve this if you'd like' or 'I don't have enough information.' If a tool returns nothing, say so explicitly (e.g., 'No notes in the last 30 days'). The phone is ringing right now; I need the answer, not a question back.",
  );
  parts.push(
    "Keep it tight — I have seconds before I pick up. Plain prose, short sections.",
  );
  parts.push(
    "Hard rail: written brief only. If you are about to call a tool that would WRITE, UPDATE, MOVE, DELETE, or SEND anything, stop and describe what you would have done instead. Read-only tools (fetch contact, list notes, search opps, get conversations) are encouraged.",
  );
  return parts.join(" ");
}

// Tagged deep-link payloads. `inbound-call` drives the InboundCallPanel
// (structured brief, no LLM in the critical path); `chat-seed` is the legacy
// escape hatch that seeds AI Assistant directly with a verbatim prompt.
type DeepLinkSeed =
  | { kind: "inbound-call"; from: string; callId: string | null; contactId: string | null; name: string | null; company: string | null }
  | { kind: "chat-seed"; prompt: string; title: string | null };

// Reads deep-link params from the URL once on mount. Two shapes:
//   ?ai=1&intent=inbound-call&from=...&contactId=...&name=...&company=...
//     → renders the InboundCallPanel; AI Assistant is opt-in via "Brief me with AI".
//   ?ai=1&prompt=...&title=...
//     → seeds the chat directly with the verbatim prompt (ad-hoc deep-links).
function readDeepLinkSeed(): DeepLinkSeed | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get("ai") !== "1") return null;

  if (params.get("intent") === "inbound-call") {
    const from = params.get("from");
    if (!from) return null;
    return {
      kind: "inbound-call",
      from,
      callId: params.get("callId"),
      contactId: params.get("contactId"),
      name: params.get("name"),
      company: params.get("company"),
    };
  }

  const prompt = params.get("prompt");
  if (!prompt) return null;
  return { kind: "chat-seed", prompt, title: params.get("title") };
}

export const CRM: React.FC = () => {
  const styles = useStyles();
  const crmApi = useCrmApiClient();
  // Deep-link state: either inbound-call (auto-switches to the Calls tab,
  // signals which callId to focus on) or chat-seed (legacy — seeds AI
  // Assistant directly). Inbound deep-links only honored when the Calls
  // feature is enabled — otherwise the deep-link is ignored and the user
  // falls back to the default tab.
  const callsEnabled = getConfig().enableCalls;
  const deepLinkSeed = useRef(readDeepLinkSeed()).current;
  const chatSeed = deepLinkSeed?.kind === "chat-seed" ? deepLinkSeed : null;
  const inboundSeed = deepLinkSeed?.kind === "inbound-call" && callsEnabled ? deepLinkSeed : null;
  const focusCallId = inboundSeed?.callId ?? null;
  // Default tab is resolved after /api/tmd/me returns: LERTs and users with
  // crm_assistant_admin land on Tackle My Day; everyone else lands on AI
  // Assistant. Null during the one-request resolve window.
  // Inbound deep-link forces the Calls tab. Chat-seed forces the AI Assistant.
  const [selectedTab, setSelectedTab] = useState<TabValue | null>(
    inboundSeed ? "calls" : chatSeed ? "chat" : null,
  );
  const [pendingChatInput, setPendingChatInput] = useState<string | null>(chatSeed?.prompt ?? null);
  const [pendingChatTitle, setPendingChatTitle] = useState<string | null>(chatSeed?.title ?? null);
  const [pendingChatAutoSend, setPendingChatAutoSend] = useState<boolean>(false);
  // Red-tab indicator: lit when an inbound call rings/is in progress and the
  // user hasn't yet looked at the Calls tab. Cleared when they switch to it.
  const [callAlert, setCallAlert] = useState(false);
  // Tabs we've mounted at least once. Kept alive via display:none so TMD's
  // briefing + expanded-notes state survives tab switches (and so a pending
  // chat input handed from TMD's Ask-Photon button lands in a fresh mount).
  const [visitedTabs, setVisitedTabs] = useState<Set<TabValue>>(
    inboundSeed ? new Set(["calls"]) : chatSeed ? new Set(["chat"]) : new Set(),
  );

  // Usage tracking: record which CRM sub-tab (Sales Assistant feature) is
  // viewed, so adoption reporting can tell Tackle My Day vs Sales Overview vs
  // AI Assistant apart. CAD/SharePoint already emit subtool_access events;
  // CRM never did. A single effect on selectedTab covers every path the tab
  // changes (async default resolve, tab click, Ask-Photon handoff, deep-link);
  // the ref dedupes so re-renders don't double-count the same tab.
  const { trackSubtoolAccess } = useUsageTracking();
  const lastTrackedTabRef = useRef<TabValue | null>(null);
  useEffect(() => {
    if (selectedTab && lastTrackedTabRef.current !== selectedTab) {
      lastTrackedTabRef.current = selectedTab;
      trackSubtoolAccess("crm", String(selectedTab));
    }
  }, [selectedTab, trackSubtoolAccess]);

  // Strip the ?ai=...&prompt=... params from the URL after consuming so a
  // browser refresh doesn't replay the prompt.
  useEffect(() => {
    if (deepLinkSeed && typeof window !== "undefined") {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [deepLinkSeed]);

  // Subscribe to broker events to drive the Calls-tab red indicator.
  useEffect(() => {
    if (!callsEnabled) return;
    const unsub = onBrokerEvent((msg) => {
      if (msg.type !== "event" || msg.payload?.event !== "call") return;
      const t = (msg.payload.data as { type?: string })?.type;
      if (t === "incoming" || t === "incoming-enriched") {
        // eslint-disable-next-line no-console
        console.log("[CRM] inbound call → setting tab alert");
        setCallAlert(true);
      }
    });
    return () => unsub();
  }, [callsEnabled]);

  // Clear the alert as soon as the user is looking at the Calls tab.
  useEffect(() => {
    if (selectedTab === "calls" && callAlert) setCallAlert(false);
  }, [selectedTab, callAlert]);

  const isConfigured = crmApi.isConfigured();

  // Resolve the default landing tab from TMD's /me (is_lert OR
  // can_impersonate_lert → TMD; else chat). If TMD is disabled or the fetch
  // fails, fall back to chat so the UI is never stuck on the spinner.
  //
  // crmApi from useCrmApiClient() is a fresh object on every render, so it's
  // stashed in a ref. Without this, the effect would re-fire on every render,
  // cancelling the in-flight /me fetch before it could complete — the
  // resolve would never finish and selectedTab would stay null forever.
  const crmApiRef = React.useRef(crmApi);
  crmApiRef.current = crmApi;
  useEffect(() => {
    if (!isConfigured) return;
    // Deep-link already forced a tab (calls or chat); don't override.
    if (chatSeed || inboundSeed) return;
    if (!getConfig().enableTackleMyDay) {
      setSelectedTab("chat");
      setVisitedTabs(new Set(["chat"]));
      return;
    }
    let cancelled = false;
    (async () => {
      let initial: TabValue = "chat";
      try {
        const me = await crmApiRef.current.get<{
          is_lert: boolean;
          can_impersonate_lert?: boolean;
          profile?: { profile_id?: string } | null;
        }>("/api/tmd/me");
        // Any user with a TMD profile (LERT, existing_customer, pm) lands
        // on TMD by default. Admins who can impersonate also default here.
        if (me.is_lert || me.can_impersonate_lert || me.profile?.profile_id) {
          initial = "tackle-my-day";
        }
      } catch {
        // Keep chat default on any failure.
      }
      if (cancelled) return;
      setSelectedTab(initial);
      setVisitedTabs(new Set([initial]));
    })();
    return () => {
      cancelled = true;
    };
  }, [isConfigured, chatSeed, inboundSeed]);

  const handleTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value);
    setVisitedTabs((prev) => {
      if (prev.has(data.value)) return prev;
      const next = new Set(prev);
      next.add(data.value);
      return next;
    });
  };

  const handleAskAboutOpp = useCallback((prompt: string, title?: string, autoSend = false) => {
    setPendingChatInput(prompt);
    setPendingChatTitle(title ?? null);
    setPendingChatAutoSend(autoSend);
    setSelectedTab("chat");
    setVisitedTabs((prev) => {
      if (prev.has("chat")) return prev;
      const next = new Set(prev);
      next.add("chat");
      return next;
    });
  }, []);

  if (!isConfigured) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <div className={styles.headerIcon}>
              <People24Regular />
            </div>
            <div>
              <Text size={500} weight="semibold">CRM Assistant</Text>
              <Text size={200} style={{ display: "block", color: tokens.colorNeutralForeground3 }}>
                AI-powered CRM assistant
              </Text>
            </div>
          </div>
        </div>
        <div className={styles.notConfigured}>
          <Card style={{ maxWidth: 500, padding: tokens.spacingVerticalL, textAlign: "center" }}>
            <CardHeader
              header={<Text size={500} weight="semibold">CRM Service Not Configured</Text>}
            />
            <MessageBar intent="warning" style={{ marginTop: tokens.spacingVerticalM }}>
              <MessageBarBody>
                <MessageBarTitle>Backend Required</MessageBarTitle>
                The CRM backend service URL is not configured. Set the{" "}
                <code>REACT_APP_CRM_SERVICE_URL</code> environment variable to the CRM
                service endpoint to enable this tool.
              </MessageBarBody>
            </MessageBar>
            <Text size={200} style={{ marginTop: tokens.spacingVerticalM, color: tokens.colorNeutralForeground3 }}>
              The CRM backend service provides the AI chat and CRM API integration.
              Deploy the CRM service and configure its URL to get started.
            </Text>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Daily News — team ticker (LERT scoring leaders + upcoming shows) on one
          scrolling line, anchored at the top of the CRM Assistant on every tab.
          Replaces the redundant tool header (the breadcrumb already names it)
          and the standalone shows strip. */}
      <TmdLeaderboard crmApi={crmApi} />

      {/* Tab Navigation — TMD leftmost so the default landing tab for LERTs
          sits at the start of the strip. Calls tab handles the live phone
          console (inbound screen-pop and outbound dial). */}
      <div className={styles.tabList}>
        <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
          {getConfig().enableTackleMyDay && (
            <Tab value="tackle-my-day" icon={<Target24Regular />}>
              Tackle My Day
            </Tab>
          )}
          <Tab value="chat" icon={<Chat24Regular />}>
            AI Assistant
          </Tab>
          {callsEnabled && (
            <Tab value="calls" icon={<Phone24Regular />} className={callAlert ? styles.tabAlert : undefined}>
              Calls
            </Tab>
          )}
          <Tab value="sales-overview" icon={<DocumentTable24Regular />}>
            Sales Overview
          </Tab>
          <Tab value="new-lead" icon={<PersonAdd24Regular />}>
            New Lead
          </Tab>
        </TabList>
      </div>

      {/* Content Area */}
      <div className={styles.content}>
        {selectedTab === "chat" && (
          <ChatPanel
            crmApi={crmApi}
            pendingInput={pendingChatInput}
            onConsumePendingInput={() => { setPendingChatInput(null); setPendingChatAutoSend(false); }}
            pendingTitle={pendingChatTitle}
            onConsumePendingTitle={() => setPendingChatTitle(null)}
            pendingAutoSend={pendingChatAutoSend}
          />
        )}
        {selectedTab === "calls" && callsEnabled && (
          <CallsPanel
            crmApi={crmApi}
            focusCallId={focusCallId}
            onAskWithAI={handleAskAboutOpp}
            buildPrompt={buildInboundCallPrompt}
          />
        )}
        {selectedTab === "sales-overview" && <SalesOverviewPanel crmApi={crmApi} />}
        {selectedTab === "new-lead" && <NewLead />}
        {visitedTabs.has("tackle-my-day") && getConfig().enableTackleMyDay && (
          <div
            style={{
              display: selectedTab === "tackle-my-day" ? "flex" : "none",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <TackleMyDayPanel crmApi={crmApi} onAskAboutOpp={handleAskAboutOpp} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CRM;
